import { Stack } from "@mui/material";
import type { GoalOverview, GoalOverviewHorizon, QuestionnaireQuestionDto } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrencyShortText } from "Infrastructure/Utils/CurrencyUtils";
import { useMemo } from "react";
import { GoalLabel } from "../_Shared/Goals/GoalLabel";
import { getTimeDurationFormatted } from "../_Shared/helpers";
import { QuestionnaireGoalCard } from "./QuestionnaireGoalCard";
import { QuestionnaireQuestion } from "./QuestionnaireQuestion";
import type { InvestmentPlan } from "./types";
import type { RuleState } from "./useRuleEngine";

type Props = {
  goals: GoalOverview[];
  horizon: GoalOverviewHorizon;
  questions: QuestionnaireQuestionDto[];
  investmentPlan?: InvestmentPlan;
  ruleState: RuleState;
};

export const QuestionnaireInitBlockHorizon = ({ goals, horizon, questions, investmentPlan, ruleState }: Props) => {
  const { t } = useResource();
  const goalItems = useMemo(() => goals.map((goal) => <GoalLabel key={`${goal.guid}_${goal.groupGuid ?? ""}`} goal={goal} />), [goals]);

  if (!investmentPlan) return null;

  return (
    <QuestionnaireGoalCard
      title={t(Resources.Questionnaire.Horizon[horizon])}
      details={[
        {
          label: t(Resources.Questionnaire.GoalCard.Horizon),
          value: getTimeDurationFormatted(investmentPlan.horizonDuration, t),
        },
        {
          label: t(Resources.Questionnaire.GoalCard.TargetAmount),
          value: investmentPlan.amount?.value != null ? formatCurrencyShortText(investmentPlan.amount?.value) : "-",
        },
      ]}
    >
      <Stack spacing={Spacing.XXL}>
        <Stack spacing={Spacing.L}>{goalItems}</Stack>
        {questions.map((question) => (
          <QuestionnaireQuestion
            key={question.questionnaireQuestionID}
            question={question}
            externalId={investmentPlan.nsureInvestmentPlanExternalId}
            ruleState={ruleState}
            isInGoalCard
          />
        ))}
      </Stack>
    </QuestionnaireGoalCard>
  );
};
